import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import {
  fetchMetaAnalysis, fetchMetaCopyBatches, generateMetaAnalysis, generateMetaCopy,
  type MetaAnalysisPattern, type MetaAnalysisPayload, type MetaCopyPayload, type MetaAiReport,
  type MetaRange,
} from '../../api/meta';
import { formatDateTime } from '../../components/formatters';

function creativeName(payload: MetaAnalysisPayload, id: string): string {
  const c = payload.inputCreatives.find((x) => x.creativeId === id);
  return c?.title ?? c?.name ?? id;
}

function PatternList({ title, patterns, payload, color }: {
  title: string;
  patterns: MetaAnalysisPattern[];
  payload: MetaAnalysisPayload;
  color: 'success' | 'error';
}) {
  if (patterns.length === 0) return null;
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      <Stack spacing={1.5}>
        {patterns.map((p, i) => (
          <Box key={i}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.pattern}</Typography>
            <Typography variant="body2" color="text.secondary">{p.evidence}</Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              {p.affectedCreativeIds.map((id) => (
                <Chip key={id} size="small" variant="outlined" color={color} label={creativeName(payload, id)} sx={{ maxWidth: 260 }} />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export function MetaAnalysisPanel({ range }: { range: MetaRange }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['meta-analysis', range.from, range.to],
    queryFn: ({ signal }) => fetchMetaAnalysis(range, signal),
  });
  const mutation = useMutation({
    mutationFn: () => generateMetaAnalysis(range),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['meta-analysis'] }),
  });

  const report = query.data?.report ?? null;
  const payload = report?.status === 'ok' ? (report.payload as MetaAnalysisPayload) : null;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Analiza AI kreacji</Typography>
          <Typography variant="body2" color="text.secondary">
            Wzorce zwycięskich i przegranych kreacji dla wybranego okresu ({range.from} – {range.to})
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeRoundedIcon />}
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Analizuję… (do minuty)' : report ? 'Odśwież analizę' : 'Generuj analizę'}
        </Button>
      </Stack>

      {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>Nie udało się wygenerować analizy. Spróbuj ponownie.</Alert>}

      {report && report.status === 'unavailable' && (
        <Alert severity="warning">Analiza niedostępna: {report.statusReason}</Alert>
      )}

      {!report && !query.isLoading && (
        <Typography variant="body2" color="text.secondary">
          Brak analizy dla tego okresu — wygeneruj pierwszą.
        </Typography>
      )}

      {payload && (
        <Stack spacing={2.5}>
          <Typography variant="caption" color="text.secondary">
            Wygenerowano {formatDateTime(report!.generatedAt)} · {payload.inputCreatives.length} kreacji · {payload.imagesAnalyzed} miniatur
          </Typography>
          <Typography variant="body1">{payload.summary}</Typography>
          <Divider />
          <PatternList title="Co działa" patterns={payload.winningPatterns} payload={payload} color="success" />
          <PatternList title="Co nie działa" patterns={payload.losingPatterns} payload={payload} color="error" />
          {payload.fatigueAlerts.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Zmęczenie kreacji</Typography>
              <Stack spacing={0.5}>
                {payload.fatigueAlerts.map((a) => (
                  <Alert key={a.creativeId} severity="warning" sx={{ py: 0 }}>
                    <strong>{creativeName(payload, a.creativeId)}</strong>: {a.reason}
                  </Alert>
                ))}
              </Stack>
            </Box>
          )}
          {payload.recommendations.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Rekomendacje</Typography>
              <Stack component="ul" spacing={0.5} sx={{ pl: 3, my: 0 }}>
                {payload.recommendations.map((r, i) => (
                  <Typography key={i} component="li" variant="body2">{r}</Typography>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}
    </Paper>
  );
}

function VariantCard({ variant }: { variant: MetaCopyPayload['variants'][number] }) {
  const [copied, setCopied] = useState(false);
  const copyText = `${variant.title}\n\n${variant.body}`;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Typography variant="subtitle2">{variant.title}</Typography>
        <Tooltip title={copied ? 'Skopiowano!' : 'Kopiuj tekst'}>
          <IconButton
            size="small"
            onClick={() => {
              void navigator.clipboard.writeText(copyText);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            <ContentCopyRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>{variant.body}</Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
        <Chip size="small" label={variant.cta} />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {variant.rationale}
      </Typography>
    </Paper>
  );
}

export function MetaCopyPanel() {
  const [brief, setBrief] = useState('');
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['meta-copy-batches'],
    queryFn: ({ signal }) => fetchMetaCopyBatches(signal),
  });
  const mutation = useMutation({
    mutationFn: () => generateMetaCopy({ brief }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['meta-copy-batches'] }),
  });

  const batches = (query.data?.batches ?? []) as MetaAiReport<MetaCopyPayload>[];
  const latest = batches[0] ?? null;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6">Generator copy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Warianty tekstów reklamowych oparte na Twoich historycznie najlepszych kreacjach (ostatnie 90 dni)
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="Brief"
          placeholder="Np. Kampania na kolagen premium przed Black Friday, oferta -30%, target: kobiety 30-50 dbające o skórę"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
        />
        <Button
          variant="contained"
          sx={{ whiteSpace: 'nowrap', mt: { xs: 0, sm: 0.5 } }}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeRoundedIcon />}
          disabled={mutation.isPending || brief.trim().length < 10}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Piszę…' : 'Generuj'}
        </Button>
      </Stack>

      {mutation.isError && <Alert severity="error" sx={{ mt: 2 }}>Nie udało się wygenerować wariantów. Spróbuj ponownie.</Alert>}

      {latest && latest.status === 'unavailable' && (
        <Alert severity="warning" sx={{ mt: 2 }}>Generacja niedostępna: {latest.statusReason}</Alert>
      )}

      {latest && latest.status === 'ok' && latest.payload && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Brief: „{latest.payload.brief}" · {formatDateTime(latest.generatedAt)} · korpus {latest.payload.corpusSize} kreacji
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {latest.payload.variants.map((v, i) => (
              <VariantCard key={i} variant={v} />
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
